/**
 * Pure scoring and serialisation functions for the DIY Comparison Checklist.
 * No side-effects, no React imports, no I/O.
 */

const WEIGHT_LABELS = { 1: 'Nice to Have', 2: 'Important', 3: 'Must Have' };

/**
 * Calculate the weighted score for a single vendor on a single feature.
 * @param {number} rating   User rating (1–5; 0 = unrated)
 * @param {number} weight   Feature weight (1–3)
 * @returns {number}
 */
export const calcFeatureScore = (rating, weight) => rating * weight;

/**
 * Return the rating a specific vendor received for a specific feature.
 * @param {Object} ratings      Map of featureId → [v0, v1, v2]
 * @param {string} featureId
 * @param {number} vendorIdx    0, 1, or 2
 * @returns {number}
 */
export const getRating = (ratings, featureId, vendorIdx) =>
  (ratings[featureId] || [0, 0, 0])[vendorIdx] ?? 0;

/**
 * Calculate the total weighted score for one vendor across all selected features.
 * @param {Array<Object>} selectedFeatures  Features the user chose to include
 * @param {Object}        featureWeights    Map of featureId → weight override
 * @param {Object}        vendorRatingsMap  Map of featureId → rating for this vendor
 * @returns {number}
 */
export const calcVendorTotal = (selectedFeatures, featureWeights, vendorRatingsMap) =>
  selectedFeatures.reduce((total, f) => {
    const weight = featureWeights[f.id] ?? f.default_weight;
    const rating = vendorRatingsMap[f.id] ?? 0;
    return total + rating * weight;
  }, 0);

/**
 * Calculate the maximum possible score for the current selection.
 * (All ratings = 5, weights as configured.)
 * @param {Array<Object>} selectedFeatures
 * @param {Object}        featureWeights
 * @returns {number}
 */
export const calcMaxPossible = (selectedFeatures, featureWeights) =>
  selectedFeatures.reduce((total, f) => {
    const weight = featureWeights[f.id] ?? f.default_weight;
    return total + 5 * weight;
  }, 0);

/**
 * Build a 2-D array of rows ready for CSV serialisation.
 * @param {Object}        template
 * @param {Array<Object>} selectedFeatures
 * @param {Object}        featureWeights
 * @param {Array<Object>} vendors           Array of 3 vendor info objects
 * @param {Object}        ratings           Map featureId → [v0, v1, v2]
 * @param {Object}        comments          Map featureId → string
 * @returns {Array<Array<string|number>>}
 */
export const buildCSVRows = (template, selectedFeatures, featureWeights, vendors, ratings, comments) => {
  const rows = [];

  rows.push([`Comparison Checklist: ${template.meta.title}`]);
  rows.push([`Date: ${new Date().toLocaleDateString()}`]);
  rows.push([`Sponsor: ${vendors[0].name}${vendors[0].product_name ? ' — ' + vendors[0].product_name : ''}`]);
  rows.push([]);

  const header = ['Feature', 'Weight', 'Importance'];
  vendors.forEach(v => {
    const n = v.name || 'Competitor';
    header.push(`${n} Rating`, `${n} Weighted`);
  });
  header.push('Notes');
  rows.push(header);

  selectedFeatures.forEach(f => {
    const w = featureWeights[f.id] ?? f.default_weight;
    const fRatings = ratings[f.id] || [0, 0, 0];
    const row = [f.title, w, WEIGHT_LABELS[w]];
    fRatings.forEach(r => row.push(r, r * w));
    row.push(comments[f.id] || '');
    rows.push(row);
  });

  const totalRow = ['TOTAL SCORE', '', ''];
  vendors.forEach((_, i) => {
    const vendorMap = Object.fromEntries(
      selectedFeatures.map(f => [f.id, getRating(ratings, f.id, i)])
    );
    totalRow.push('', calcVendorTotal(selectedFeatures, featureWeights, vendorMap));
  });
  totalRow.push('');
  rows.push(totalRow);

  const maxRow = ['MAX POSSIBLE', '', ''];
  const maxScore = calcMaxPossible(selectedFeatures, featureWeights);
  vendors.forEach(() => maxRow.push('', maxScore));
  maxRow.push('');
  rows.push(maxRow);

  return rows;
};

/**
 * Serialise a 2-D array into a CSV string with quoted cells.
 * @param {Array<Array<string|number>>} rows
 * @returns {string}
 */
export const serializeCSV = (rows) =>
  rows
    .map(row => row.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
